import { useMutation, useQuery } from '@apollo/client'
import {
  payeezyClientToken,
  PayeezyClientTokenOut,
  PayeezyPaymentToken,
  payeezyPaymentTokenCreate,
  setVaultPaymentMethodOnCartMutation,
  SetVaultPaymentMethodOnCartResponse
} from '../utils'
import { useState } from 'react'
import { useCheckout } from 'lib/modules/checkout'
import { PaymentForm } from '../components/PayeezyCreditCardForm'

interface Params {
  onCreatePaymentTokenSuccess?: () => void
}

export type PayeezySubmitForm = (params: { onSuccess: (clientToken: string) => void, onError?: (error: Error) => void }) => void

export const usePayeezy = (params?: Params) => {
  const { cartId, placeOrder, lockCheckout, unlockCheckout } = useCheckout()

  const [createPaymentToken, { loading: crateTokenIsLoading }] = useMutation<{
    payeezyPaymentTokenCreate: PayeezyPaymentToken
  }>(payeezyPaymentTokenCreate, {
    onCompleted: params?.onCreatePaymentTokenSuccess
  })

  const [setVaultTokenOnCart] = useMutation<{
    setPaymentMethodOnCart: SetVaultPaymentMethodOnCartResponse
  }>(setVaultPaymentMethodOnCartMutation, {
    onCompleted: params?.onCreatePaymentTokenSuccess
  })

  const { data: keys, loading: clientIsLoading } = useQuery<{
    payeezyClientToken: PayeezyClientTokenOut
  }>(payeezyClientToken, {
    fetchPolicy: 'no-cache'
  })

  const { clientToken, publicKeyBase64 } = keys?.payeezyClientToken ?? {}
  const [isLoading, setLoading] = useState(false)
  const [paymentForm, setPaymentForm] = useState<undefined | PaymentForm>()

  const submitForm: PayeezySubmitForm = (params) => {
    if (!paymentForm) {
      return
    }

    setLoading(true)
    paymentForm.onSubmit(
      (clientToken) => {
        params.onSuccess(clientToken)
        setLoading(false)
      },
      (error) => {
        // eslint-disable-next-line
        console.info(error)
        params.onError?.(error)
        setLoading(false)
      }
    )
  }

  const placeVaultOrder = async (params: { publicHash: string, onError: (e: Error) => void }) => {
    try {
      lockCheckout()
      await setVaultTokenOnCart({
        variables: {
          publicHash: params.publicHash,
          cartId
        }
      })

      unlockCheckout()
      placeOrder()
    } catch (e) {
      params.onError(e)
      unlockCheckout()
    }
  }

  return {
    loading: isLoading || crateTokenIsLoading,
    clientIsLoading,
    paymentForm,
    setPaymentForm,
    clientToken,
    publicKeyBase64,
    createPaymentToken,
    submitForm,
    placeVaultOrder
  }
}
